import api from './api';

// 메모리 캐시 구현
const cache = new Map();

const dailyPriceService = {
  /**
   * 캐시에서 데이터 조회
   * @private
   */
  _getFromCache: (key) => {
    const cached = cache.get(key);
    if (cached && cached.expiry > Date.now()) {
      return cached.data;
    }
    if (cached) {
      cache.delete(key); // 만료된 캐시 삭제
    }
    return null;
  },

  /**
   * 캐시에 데이터 저장
   * @private
   */
  _setCache: (key, data, ttlMs = 5 * 60 * 1000) => {
    cache.set(key, {
      data: data,
      expiry: Date.now() + ttlMs
    });
  },

  /**
   * 캐시 클리어
   */
  clearCache: () => {
    cache.clear();
    // console.log('🗑️ 종가 데이터 캐시 클리어됨');
  },

  /**
   * 여러 브로커의 데이터를 병합 (같은 날짜의 데이터가 있으면 우선순위에 따라 선택)
   * @private
   * @param {Array} results - 종가 데이터 배열
   * @returns {Array} 병합된 데이터
   */
  _mergeBrokerData: (results) => {
    if (!Array.isArray(results) || results.length === 0) {
      return [];
    }

    // 브로커 우선순위 (KIS > NH > 기타)
    const brokerPriority = {
      'kis': 1,
      'nh': 2
    };

    // 날짜별로 그룹화
    const dateGroups = {};
    results.forEach(item => {
      const date = item.date;
      if (!dateGroups[date]) {
        dateGroups[date] = [];
      }
      dateGroups[date].push(item);
    });

    // 각 날짜별로 최적의 데이터 선택
    const mergedResults = [];
    Object.keys(dateGroups).forEach(date => {
      const dayData = dateGroups[date];
      
      if (dayData.length === 1) {
        // 하나의 브로커 데이터만 있으면 그대로 사용
        mergedResults.push(dayData[0]);
      } else {
        // 여러 브로커 데이터가 있으면 우선순위에 따라 선택
        const sortedData = dayData.sort((a, b) => {
          const aPriority = brokerPriority[a.broker?.toLowerCase()] || 999;
          const bPriority = brokerPriority[b.broker?.toLowerCase()] || 999;
          return aPriority - bPriority;
        });
        
        // 가장 우선순위가 높은 데이터 선택하되, 브로커 정보는 통합으로 표시
        const selectedData = { ...sortedData[0] };
        const brokers = [...new Set(dayData.map(d => d.broker))];
        selectedData.broker = brokers.length > 1 ? brokers.join('+') : selectedData.broker;
        selectedData._merged = true;
        selectedData._source_brokers = brokers;
        
        mergedResults.push(selectedData);
      }
    });

    // console.log(`🔄 브로커 데이터 병합: ${results.length}개 → ${mergedResults.length}개`);
    return mergedResults.sort((a, b) => new Date(b.date) - new Date(a.date));
  },
  /**
   * 일일 종가 데이터 조회
   * @param {Object} params - 조회 파라미터
   * @param {string} params.stock_symbol - 종목 코드
   * @param {string} params.stock_name - 종목명 (선택)
   * @param {string} params.broker - 브로커명 (선택)
   * @param {string} params.date_from - 시작 날짜 (YYYY-MM-DD)
   * @param {string} params.date_to - 종료 날짜 (YYYY-MM-DD)
   * @param {number} params.page_size - 페이지 크기 (선택)
   * @returns {Promise} API 응답
   */
  getDailyPrices: async (params = {}) => {
    try {
      // console.log('📊 일일 종가 데이터 조회:', params);
      
      const response = await api.get('/api/data_predator/daily-stock-prices/', {
        params: params
      });
      
      // console.log('✅ 일일 종가 데이터 조회 성공:', response.data);
      return response;
    } catch (error) {
      console.error('❌ 일일 종가 데이터 조회 실패:', error);
      throw error;
    }
  },

  /**
   * 특정 종목의 일일 종가 데이터 조회 (모든 브로커 데이터 통합)
   * @param {string} stockSymbol - 종목 코드
   * @param {string} dateFrom - 시작 날짜 (YYYY-MM-DD)
   * @param {string} dateTo - 종료 날짜 (YYYY-MM-DD)
   * @param {string} broker - 브로커명 (선택, 없으면 모든 브로커 데이터)
   * @returns {Promise} API 응답
   */
  getStockPriceHistory: async (stockSymbol, dateFrom, dateTo, broker = null) => {
    try {
      // console.log(`📈 ${stockSymbol} 종가 히스토리 조회:`, { dateFrom, dateTo, broker: broker || 'all' });
      
      const params = {
        stock_symbol: stockSymbol,
        date_from: dateFrom,
        date_to: dateTo
      };
      
      // broker가 명시적으로 지정된 경우에만 추가
      if (broker) {
        params.broker = broker;
      }
      
      const response = await api.get('/api/data_predator/daily-stock-prices/', {
        params: params
      });
      
      // 모든 브로커 데이터를 가져온 경우 중복 제거 및 병합
      if (!broker && response.data.results) {
        response.data.results = dailyPriceService._mergeBrokerData(response.data.results);
      }
      
      // console.log(`✅ ${stockSymbol} 종가 히스토리 조회 성공:`, response.data);
      return response;
    } catch (error) {
      console.error(`❌ ${stockSymbol} 종가 히스토리 조회 실패:`, error);
      throw error;
    }
  },

  /**
   * 특정 날짜의 모든 종목 종가 데이터 조회 (모든 브로커 데이터 통합)
   * @param {string} date - 날짜 (YYYY-MM-DD)
   * @param {string} broker - 브로커명 (선택, 없으면 모든 브로커 데이터)
   * @returns {Promise} API 응답
   */
  getDailyPricesByDate: async (date, broker = null) => {
    try {
      // console.log(`📅 ${date} 일일 종가 데이터 조회:`, { broker: broker || 'all' });
      
      const params = {
        date_from: date,
        date_to: date
      };
      
      // broker가 명시적으로 지정된 경우에만 추가
      if (broker) {
        params.broker = broker;
      }
      
      const response = await api.get('/api/data_predator/daily-stock-prices/', {
        params: params
      });
      
      // 모든 브로커 데이터를 가져온 경우 중복 제거 및 병합
      if (!broker && response.data.results) {
        response.data.results = dailyPriceService._mergeBrokerData(response.data.results);
      }
      
      // console.log(`✅ ${date} 일일 종가 데이터 조회 성공:`, response.data);
      return response;
    } catch (error) {
      console.error(`❌ ${date} 일일 종가 데이터 조회 실패:`, error);
      throw error;
    }
  },

  /**
   * 일일 종가 통계 데이터 조회
   * @returns {Promise} API 응답
   */
  getDailyPriceStats: async () => {
    try {
      // console.log('📊 일일 종가 통계 조회');
      
      const response = await api.get('/api/data_predator/daily-stock-prices/stats/');
      
      // console.log('✅ 일일 종가 통계 조회 성공:', response.data);
      return response;
    } catch (error) {
      console.error('❌ 일일 종가 통계 조회 실패:', error);
      throw error;
    }
  },

  /**
   * 보유 종목들의 최근 종가 데이터 조회 (최적화된 다중 종목 API 사용, 모든 브로커 데이터 통합)
   * @param {Array} stockSymbols - 종목 코드 배열
   * @param {number} days - 최근 며칠 (기본값: 30일, dateFrom/dateTo 미지정 시 사용)
   * @param {string} broker - 브로커명 (선택, 없으면 모든 브로커 데이터)
   * @param {string} dateFrom - 시작 날짜 (YYYY-MM-DD, 선택)
   * @param {string} dateTo - 종료 날짜 (YYYY-MM-DD, 선택)
   * @returns {Promise} 종목별 종가 데이터
   */
  getHoldingsRecentPrices: async (stockSymbols, days = 30, broker = null, dateFrom = null, dateTo = null) => {
    try {
      // console.log('📈 보유 종목 최근 종가 조회 (최적화):', { stockSymbols, days, broker: broker || 'all' });
      
      if (!stockSymbols || stockSymbols.length === 0) {
        return {};
      }

      // 캐시 키 생성 (broker가 null이면 'all'로 표시)
      const sortedSymbols = [...stockSymbols].sort();
      const rangeKey = dateFrom && dateTo ? `${dateFrom}_${dateTo}` : `recent_${days}`;
      const cacheKey = `holdings_prices_${sortedSymbols.join(',')}_${rangeKey}_${broker || 'all'}`;
      
      // 캐시에서 먼저 확인
      const cachedData = dailyPriceService._getFromCache(cacheKey);
      if (cachedData) {
        // console.log('✅ 캐시에서 보유 종목 데이터 반환:', stockSymbols.length, '개 종목');
        return cachedData;
      }
      
      // 날짜 계산: 명시 날짜 범위가 있으면 그대로 사용하고, 없으면 기존처럼 최근 N일 조회
      let requestDateFrom = dateFrom;
      let requestDateTo = dateTo;
      if (!requestDateFrom || !requestDateTo) {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(endDate.getDate() - days);
        
        requestDateFrom = startDate.toISOString().split('T')[0];
        requestDateTo = endDate.toISOString().split('T')[0];
      }
      
      // API 파라미터 구성
      const params = {
        stock_symbols: stockSymbols,  // 배열로 전달
        date_from: requestDateFrom,
        date_to: requestDateTo
      };
      
      // broker가 명시적으로 지정된 경우에만 추가
      if (broker) {
        params.broker = broker;
      }
      
      // 다중 종목 API 호출 (한 번의 요청으로 모든 종목 데이터 조회)
      const response = await api.get('/api/data_predator/daily-stock-prices/', {
        params: params
      });
      
      // 백엔드에서 grouped_results를 제공하면 사용, 없으면 직접 그룹화
      let groupedResults = response.data.grouped_results || {};
      
      // grouped_results가 없으면 results를 직접 그룹화
      if (Object.keys(groupedResults).length === 0 && response.data.results) {
        // 모든 브로커 데이터를 가져온 경우 먼저 병합
        const mergedResults = !broker ? dailyPriceService._mergeBrokerData(response.data.results) : response.data.results;
        
        mergedResults.forEach(item => {
          const symbol = item.stock_symbol;
          if (!groupedResults[symbol]) {
            groupedResults[symbol] = [];
          }
          groupedResults[symbol].push(item);
        });
      }
      
      // 결과를 기존 형식으로 변환
      const holdingsPrices = {};
      stockSymbols.forEach(symbol => {
        holdingsPrices[symbol] = {
          data: groupedResults[symbol] || [],
          success: true,
          error: null
        };
      });
      
      // 캐시에 저장 (5분)
      dailyPriceService._setCache(cacheKey, holdingsPrices, 5 * 60 * 1000);
      
      // console.log('✅ 보유 종목 최근 종가 조회 성공 (최적화):', Object.keys(holdingsPrices).length, '개 종목');
      return holdingsPrices;
      
    } catch (error) {
      console.error('❌ 보유 종목 최근 종가 조회 실패:', error);
      
      // 폴백: 개별 API 호출
      // console.log('🔄 폴백: 개별 API 호출로 재시도');
      return dailyPriceService._getHoldingsRecentPricesFallback(stockSymbols, days, broker, dateFrom, dateTo);
    }
  },

  /**
   * 폴백: 개별 API 호출로 보유 종목 데이터 조회
   * @private
   */
  _getHoldingsRecentPricesFallback: async (stockSymbols, days, broker, dateFrom = null, dateTo = null) => {
    try {
      // 날짜 계산: 명시 날짜 범위가 있으면 그대로 사용하고, 없으면 기존처럼 최근 N일 조회
      let requestDateFrom = dateFrom;
      let requestDateTo = dateTo;
      if (!requestDateFrom || !requestDateTo) {
        const endDate = new Date();
        const startDate = new Date();
        startDate.setDate(endDate.getDate() - days);
        
        requestDateFrom = startDate.toISOString().split('T')[0];
        requestDateTo = endDate.toISOString().split('T')[0];
      }
      
      // 각 종목별로 데이터 조회 (기존 방식)
      const promises = stockSymbols.map(symbol => 
        dailyPriceService.getStockPriceHistory(symbol, requestDateFrom, requestDateTo, broker)
          .then(response => ({
            symbol,
            data: response.data.results || [],
            success: true
          }))
          .catch(error => ({
            symbol,
            data: [],
            success: false,
            error: error.message
          }))
      );
      
      const results = await Promise.all(promises);
      
      // 결과를 객체 형태로 변환
      const holdingsPrices = {};
      results.forEach(result => {
        holdingsPrices[result.symbol] = {
          data: result.data,
          success: result.success,
          error: result.error
        };
      });
      
      // console.log('✅ 폴백으로 보유 종목 최근 종가 조회 성공:', holdingsPrices);
      return holdingsPrices;
    } catch (error) {
      console.error('❌ 폴백 보유 종목 최근 종가 조회 실패:', error);
      throw error;
    }
  },

  /**
   * 차트용 종가 데이터 포맷팅
   * @param {Array} priceData - 종가 데이터 배열
   * @returns {Object} 차트용 포맷된 데이터
   */
  formatPriceDataForChart: (priceData) => {
    if (!Array.isArray(priceData) || priceData.length === 0) {
      return {
        categories: [],
        series: []
      };
    }

    // 날짜순으로 정렬
    const sortedData = [...priceData].sort((a, b) => new Date(a.date) - new Date(b.date));

    const categories = sortedData.map(item => item.date);
    const closingPrices = sortedData.map(item => parseFloat(item.closing_price));
    const volumes = sortedData.map(item => parseInt(item.volume) || 0);

    return {
      categories,
      series: [
        {
          name: '종가',
          type: 'line',
          data: closingPrices,
          yAxisIndex: 0
        },
        {
          name: '거래량',
          type: 'bar',
          data: volumes,
          yAxisIndex: 1
        }
      ]
    };
  },

  /**
   * 종가 변화율 계산
   * @param {Array} priceData - 종가 데이터 배열 (날짜순 정렬됨)
   * @returns {Array} 변화율이 포함된 데이터
   */
  calculatePriceChanges: (priceData) => {
    if (!Array.isArray(priceData) || priceData.length === 0) {
      return [];
    }

    // 날짜순으로 정렬
    const sortedData = [...priceData].sort((a, b) => new Date(a.date) - new Date(b.date));

    return sortedData.map((item, index) => {
      let changeFromPrev = 0;
      let changePercentFromPrev = 0;

      // 백엔드에서 계산된 값이 있으면 우선 사용
      const backendChangeRate = parseFloat(item.change_rate || 0);
      const backendChangeAmount = parseFloat(item.change_amount || 0);

      if (backendChangeRate !== 0 || backendChangeAmount !== 0) {
        // 백엔드에서 계산된 값 사용
        changeFromPrev = backendChangeAmount;
        changePercentFromPrev = backendChangeRate;
      } else if (index > 0) {
        // 프론트엔드에서 계산
        const prevPrice = parseFloat(sortedData[index - 1].closing_price);
        const currentPrice = parseFloat(item.closing_price);
        changeFromPrev = currentPrice - prevPrice;
        changePercentFromPrev = prevPrice !== 0 ? (changeFromPrev / prevPrice) * 100 : 0;
      }

      return {
        ...item,
        change_from_prev: changeFromPrev,
        change_percent_from_prev: changePercentFromPrev
      };
    });
  },

  /**
   * 종목별 최신 종가 정보 조회 (모든 브로커 데이터 통합)
   * @param {Array} stockSymbols - 종목 코드 배열
   * @param {string} broker - 브로커명 (선택, 없으면 모든 브로커 데이터)
   * @returns {Promise} 종목별 최신 종가 정보
   */
  getLatestPrices: async (stockSymbols, broker = null) => {
    try {
      // console.log('📊 최신 종가 정보 조회:', { stockSymbols, broker: broker || 'all' });
      
      // 각 종목의 최신 데이터 조회
      const promises = stockSymbols.map(async (symbol) => {
        try {
          const params = {
            stock_symbol: symbol,
            page_size: 1 // 최신 1개만
          };
          
          // broker가 명시적으로 지정된 경우에만 추가
          if (broker) {
            params.broker = broker;
          }
          
          const response = await api.get('/api/data_predator/daily-stock-prices/', {
            params: params
          });
          
          // 모든 브로커 데이터를 가져온 경우 병합
          let latestData = response.data.results?.[0];
          if (!broker && response.data.results && response.data.results.length > 0) {
            const mergedResults = dailyPriceService._mergeBrokerData(response.data.results);
            latestData = mergedResults[0];
          }
          
          return {
            symbol,
            data: latestData,
            success: true
          };
        } catch (error) {
          return {
            symbol,
            data: null,
            success: false,
            error: error.message
          };
        }
      });
      
      const results = await Promise.all(promises);
      
      // 결과를 객체 형태로 변환
      const latestPrices = {};
      results.forEach(result => {
        latestPrices[result.symbol] = {
          data: result.data,
          success: result.success,
          error: result.error
        };
      });
      
      // console.log('✅ 최신 종가 정보 조회 성공:', latestPrices);
      return latestPrices;
    } catch (error) {
      console.error('❌ 최신 종가 정보 조회 실패:', error);
      throw error;
    }
  },

  /**
   * 포트폴리오 추이 데이터 조회 (실제 보유량 기반)
   * @param {Object} params - 조회 파라미터
   * @param {string} params.date_from - 시작 날짜 (YYYY-MM-DD)
   * @param {string} params.date_to - 종료 날짜 (YYYY-MM-DD)
   * @param {Array} params.account_ids - 계좌 ID 배열 (선택)
   * @param {number} params.days - 최근 며칠 (date_from, date_to 대신 사용 가능)
   * @returns {Promise} 포트폴리오 추이 데이터
   */
  getPortfolioTrend: async (params = {}) => {
    try {
      // console.log('📈 포트폴리오 추이 데이터 조회 (실제 보유량 기반):', params);
      
      // 캐시 키 생성
      const cacheKey = `portfolio_trend_${JSON.stringify(params)}`;
      
      // 캐시에서 먼저 확인
      const cachedData = dailyPriceService._getFromCache(cacheKey);
      if (cachedData) {
        // console.log('✅ 캐시에서 포트폴리오 추이 데이터 반환');
        return cachedData;
      }
      
      const response = await api.get('/api/data_predator/portfolio-trend/', {
        params: params
      });
      
      // 캐시에 저장 (3분)
      dailyPriceService._setCache(cacheKey, response.data, 3 * 60 * 1000);
      
      // console.log('✅ 포트폴리오 추이 데이터 조회 성공:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ 포트폴리오 추이 데이터 조회 실패:', error);
      throw error;
    }
  },

  /**
   * 특정 날짜의 상세 정보 조회 (배당, 수량 변화 등)
   * @param {string} date - 날짜 (YYYY-MM-DD)
   * @returns {Promise} 날짜 상세 정보 (배당, 수량 변화)
   */
  getDailyHoldingDateDetails: async (date) => {
    try {
      // console.log('📅 날짜 상세 정보 조회:', date);
      
      // 캐시 키 생성
      const cacheKey = `date_details_${date}`;
      
      // 캐시에서 먼저 확인
      const cachedData = dailyPriceService._getFromCache(cacheKey);
      if (cachedData) {
        // console.log('✅ 캐시에서 날짜 상세 정보 반환');
        return cachedData;
      }
      
      const response = await api.get('/api/data_predator/daily-holdings/date-details/', {
        params: { date }
      });
      
      // 캐시에 저장 (5분)
      dailyPriceService._setCache(cacheKey, response.data, 5 * 60 * 1000);
      
      // console.log('✅ 날짜 상세 정보 조회 성공:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ 날짜 상세 정보 조회 실패:', error);
      throw error;
    }
  }
};

export default dailyPriceService;
